import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { formatPrice } from './utils/priceFormatter';
import { serviceTranslations } from './translations';
import './AdminServicesList.css';

function AdminServicesList() {
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [isSequenceMode, setIsSequenceMode] = useState(false);
  const [sequenceServices, setSequenceServices] = useState([]);
  const [isSavingSequence, setIsSavingSequence] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const { data, error: fetchError } = await supabase
        .from('services')
        .select('*')
        .order('display_order', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      setServices(data || []);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError('Failed to load services. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (serviceId, serviceName) => {
    try {
      // Check if there are any bookings for this service (for user information)
      const { data: bookings } = await supabase
        .from('bookings')
        .select('id')
        .eq('service_id', serviceId);

      const bookingsCount = bookings?.length || 0;

      // Delete the service (database will automatically set service_id to NULL in bookings if constraint is updated)
      const { error: deleteError } = await supabase
        .from('services')
        .delete()
        .eq('id', serviceId);

      if (deleteError) {
        // Provide helpful error message for foreign key constraint violations
        if (deleteError.code === '23503') {
          throw new Error(
            `Cannot delete service: ${bookingsCount} booking(s) are linked to this service. ` +
            `Please run the fix_service_deletion_constraint.sql script in Supabase SQL Editor ` +
            `to allow service deletion, or cancel/complete all bookings first.`
          );
        }
        throw deleteError;
      }

      // Remove from local state
      setServices(services.filter(s => s.id !== serviceId));
      setDeleteConfirm(null);
      
      // Inform user if bookings were affected
      if (bookingsCount > 0) {
        alert(`Service deleted successfully. ${bookingsCount} related booking(s) were updated (service reference removed).`);
      }
    } catch (err) {
      console.error('Error deleting service:', err);
      const errorMessage = err.message || 'Failed to delete service. Please try again.';
      alert(errorMessage);
    }
  };

  const handleEdit = (serviceId) => {
    navigate(`/admin-dashboard/services/edit/${serviceId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSetSequence = () => {
    // Sort services by display_order, then by created_at
    const sorted = [...services].sort((a, b) => {
      if (a.display_order === null && b.display_order === null) {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      if (a.display_order === null) return 1;
      if (b.display_order === null) return -1;
      return a.display_order - b.display_order;
    });
    setSequenceServices(sorted);
    setIsSequenceMode(true);
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newSequence = [...sequenceServices];
    [newSequence[index - 1], newSequence[index]] = [newSequence[index], newSequence[index - 1]];
    setSequenceServices(newSequence);
  };

  const handleMoveDown = (index) => {
    if (index === sequenceServices.length - 1) return;
    const newSequence = [...sequenceServices];
    [newSequence[index], newSequence[index + 1]] = [newSequence[index + 1], newSequence[index]];
    setSequenceServices(newSequence);
  };

  const handleSaveSequence = async () => {
    try {
      setIsSavingSequence(true);
      
      // Update display_order for all services
      const updates = sequenceServices.map((service, index) => ({
        id: service.id,
        display_order: index + 1
      }));

      // Batch update all services
      for (const update of updates) {
        const { error: updateError } = await supabase
          .from('services')
          .update({ display_order: update.display_order })
          .eq('id', update.id);

        if (updateError) {
          throw updateError;
        }
      }

      // Refresh services list
      await fetchServices();
      setIsSequenceMode(false);
      setShowSuccessPopup(true);
      // Auto-hide after 3 seconds
      setTimeout(() => {
        setShowSuccessPopup(false);
      }, 3000);
    } catch (err) {
      console.error('Error saving sequence:', err);
      alert('Failed to save sequence. Please try again.');
    } finally {
      setIsSavingSequence(false);
    }
  };

  const handleCancelSequence = () => {
    setIsSequenceMode(false);
    setSequenceServices([]);
  };

  const hasTranslation = (service) => {
    // Check if service has database translations
    const hasDbTranslations = (service.name_ar || service.name_ru) && 
                              (service.description_ar || service.description_ru);
    
    // Check if service has file-based translations
    const hasFileTranslations = !!serviceTranslations[service.name];
    
    return hasDbTranslations || hasFileTranslations;
  };

  const filteredServices = services.filter(service => {
    const searchLower = searchTerm.toLowerCase();
    return (
      service.name?.toLowerCase().includes(searchLower) ||
      service.description?.toLowerCase().includes(searchLower) ||
      service.type?.toLowerCase().includes(searchLower)
    );
  });

  const servicesWithoutTranslations = services.filter(service => !hasTranslation(service));

  if (isLoading) {
    return (
      <div className="admin-services-list-page">
        <div className="services-loading">
          <div className="loading-spinner"></div>
          <p>Loading services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-services-list-page">
      <div className="services-list-header">
        <div>
          <h1 className="services-list-title">All Services</h1>
          <p className="services-list-subtitle">Manage your service offerings</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button 
            className="set-sequence-btn"
            onClick={handleSetSequence}
            disabled={services.length === 0}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M7 12h10M11 18h6"></path>
            </svg>
            Set Sequence
          </button>
          <button 
            className="add-service-btn"
            onClick={() => navigate('/admin-dashboard/services/add')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Add New Service
          </button>
        </div>
      </div>

      <div className="services-list-controls">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="text"
            placeholder="Search services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="services-count">
          Total Services: <span className="count-number">{filteredServices.length}</span>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          {error}
          <button onClick={fetchServices} className="retry-btn">Retry</button>
        </div>
      )}

      {servicesWithoutTranslations.length > 0 && (
        <div className="translation-warning-banner">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2L2 22h20L12 2z"></path>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <div>
            <strong>{servicesWithoutTranslations.length} service(s) missing translations:</strong>
            <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem' }}>
              {servicesWithoutTranslations.map(service => (
                <li key={service.id} style={{ margin: '0.25rem 0' }}>{service.name}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="services-grid-container">
        {filteredServices.length > 0 ? (
          <div className="services-grid">
            {filteredServices.map((service) => (
              <div key={service.id} className="service-card-admin">
                <div className="service-card-image-wrapper">
                  <img 
                    src={service.image || 'https://via.placeholder.com/400x300'} 
                    alt={service.name}
                    className="service-card-image"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300';
                    }}
                  />
                  <div className="service-type-badge-admin">
                    {service.type}
                  </div>
                  {service.has_audio && (
                    <div className="audio-badge-admin">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                      </svg>
                    </div>
                  )}
                </div>
                <div className="service-card-content-admin">
                  <h3 className="service-card-name-admin">{service.name}</h3>
                  <div className="service-meta-admin">
                    <span className="service-duration-admin">{service.duration}</span>
                    <span className="service-price-admin">{formatPrice(service.price)}</span>
                  </div>
                  <p className="service-description-admin">
                    {service.description.length > 100 
                      ? service.description.substring(0, 100) + '...' 
                      : service.description}
                  </p>
                  <div className="service-card-actions">
                    <button 
                      className="edit-btn"
                      onClick={() => handleEdit(service.id)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                      Edit
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => setDeleteConfirm(service.id)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                      Delete
                    </button>
                  </div>
                  <div className="service-date-admin">
                    Created: {formatDate(service.created_at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-services">
            <div className="empty-icon">✨</div>
            <h3>No Services Found</h3>
            <p>
              {searchTerm
                ? 'No services match your search criteria.'
                : 'No services have been added yet.'}
            </p>
            {!searchTerm && (
              <button 
                className="add-first-service-btn"
                onClick={() => navigate('/admin-dashboard/services/add')}
              >
                Add Your First Service
              </button>
            )}
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="clear-search-btn">
                Clear Search
              </button>
            )}
          </div>
        )}
      </div>

      {/* Sequence Mode Modal */}
      {isSequenceMode && (
        <div className="sequence-modal-overlay" onClick={handleCancelSequence}>
          <div className="sequence-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sequence-modal-header">
              <h3>Set Service Sequence</h3>
              <p>Reorder services by clicking the up/down arrows. The order will be saved to the database.</p>
            </div>
            <div className="sequence-list">
              {sequenceServices.map((service, index) => (
                <div key={service.id} className="sequence-item">
                  <div className="sequence-item-number">{index + 1}</div>
                  <div className="sequence-item-content">
                    <h4>{service.name}</h4>
                    <p className="sequence-item-meta">{service.type} • {formatPrice(service.price)}</p>
                  </div>
                  <div className="sequence-item-actions">
                    <button
                      className="sequence-move-btn"
                      onClick={() => handleMoveUp(index)}
                      disabled={index === 0}
                      title="Move up"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="18 15 12 9 6 15"></polyline>
                      </svg>
                    </button>
                    <button
                      className="sequence-move-btn"
                      onClick={() => handleMoveDown(index)}
                      disabled={index === sequenceServices.length - 1}
                      title="Move down"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="sequence-modal-actions">
              <button 
                className="cancel-btn"
                onClick={handleCancelSequence}
                disabled={isSavingSequence}
              >
                Cancel
              </button>
              <button 
                className="save-sequence-btn"
                onClick={handleSaveSequence}
                disabled={isSavingSequence}
              >
                {isSavingSequence ? 'Saving...' : 'Save Sequence'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="success-popup-overlay" onClick={() => setShowSuccessPopup(false)}>
          <div className="success-popup" onClick={(e) => e.stopPropagation()}>
            <div className="success-popup-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" className="success-circle"/>
                <path d="M8 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="success-check"/>
              </svg>
            </div>
            <h3 className="success-popup-title">Sequence Saved!</h3>
            <p className="success-popup-message">Service order has been updated successfully.</p>
            <button 
              className="success-popup-close"
              onClick={() => setShowSuccessPopup(false)}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="delete-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this service? This action cannot be undone.</p>
            <div className="delete-modal-actions">
              <button 
                className="cancel-btn"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn"
                onClick={() => {
                  const service = services.find(s => s.id === deleteConfirm);
                  handleDelete(deleteConfirm, service?.name);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminServicesList;

